import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/fetch-api';
import { toast } from '@/hooks/use-toast';
import { mensagemDoErro, STOCK_ERROR_MESSAGES } from '@/components/stock/stockErrorMessages';
import type { AvisoReservaDescoberta, DevolucaoFornecedor, Material, Movimento, MotivoRetirada, ParteCortada, SaldoMaterial, StockPackage, StockUnit } from '@/types/stock';

/**
 * Estoque — dados do módulo (migration 0080).
 *
 * Passa pela rota dedicada /api/stock e NÃO pelo /api/db genérico: as tabelas
 * ficam fora de GLOBAL_TABLES (o adapter não tem gate de role por tabela) e
 * dentro de WRITE_BLOCKED_TABLES.
 *
 * ⚠️ AS ROTAS RESPONDEM 200 MESMO NO ERRO DE NEGÓCIO, com { ok:false, code } —
 * a Cloudflare substitui o corpo de qualquer 5xx e a mensagem útil se perde.
 * Como o status é 200, o `fetchApi` NÃO lança: o erro chega no VALOR DE RETORNO.
 * Por isso `chamar()` converte {ok:false} num throw tipado — assim as telas
 * usam onError normalmente, sem cada uma reinventar a checagem. Escrever
 * `if (code === ...)` sem esse helper seria código morto.
 *
 * Estas queries NÃO entram em `invalidateProductionQueries`: um evento de
 * production_steps não pode refetchar saldo (é o mecanismo do storm de 18/mai).
 */

export const stockKeys = {
  all: ['stock'] as const,
  balances: (filtros?: Record<string, unknown>) => ['stock', 'balances', filtros ?? {}] as const,
  materials: () => ['stock', 'materials'] as const,
  units: (materialId?: string) => ['stock', 'units', materialId ?? 'todos'] as const,
  movements: (materialId?: string) => ['stock', 'movements', materialId ?? 'todos'] as const,
  scan: (barcode: string) => ['stock', 'scan', barcode] as const,
  packages: (filtros?: Record<string, unknown>) => ['stock', 'packages', filtros ?? {}] as const,
  devolucoes: () => ['stock', 'devolucoes'] as const,
};

export class StockError extends Error {
  code: string;
  detalhes: unknown;
  constructor(code: string, mensagem: string, detalhes?: unknown) {
    super(mensagem);
    this.name = 'StockError';
    this.code = code;
    this.detalhes = detalhes;
  }
}

interface Envelope<T> {
  ok: boolean;
  data?: T;
  code?: string;
  error?: string;
  detalhes?: unknown;
}

/** Converte o envelope em dado ou throw tipado. */
export async function chamarEstoque<T>(
  path: string,
  options?: { method?: string; body?: unknown },
): Promise<T> {
  const resp = (await fetchApi(path, {
    method: options?.method ?? 'GET',
    body: options?.body,
  })) as Envelope<T>;

  if (!resp?.ok) {
    const m = mensagemDoErro(resp?.code);
    throw new StockError(resp?.code ?? 'ERRO_INTERNO', resp?.error ?? m.titulo, resp?.detalhes);
  }
  return resp.data as T;
}

/**
 * Erro do estoque vira toast que diz o que fazer, nunca um código cru.
 *
 * A frase que o SERVIDOR mandou (`err.message`) entra na frente, quando o código é conhecido:
 * ela carrega o que o texto fixo não sabe — "aguardando a produção EST-1789754611697", "faltam
 * 3 peças". Código desconhecido não repete a mensagem crua (podia ser texto técnico do banco).
 */
export function toastErroEstoque(err: unknown) {
  const code = err instanceof StockError ? err.code : undefined;
  const m = mensagemDoErro(code);
  const doServidor = err instanceof StockError && code && STOCK_ERROR_MESSAGES[code]
    && err.message && err.message !== m.titulo && err.message !== m.oQueFazer
    ? err.message.trim()
    : null;
  toast({
    title: m.titulo,
    description: doServidor ? `${doServidor} ${m.oQueFazer}` : m.oQueFazer,
    variant: 'destructive',
  });
}

/**
 * `orderId`: as reservas ATIVAS DESTE pedido não descontam do `disponivel` —
 * reprogramar substitui a reserva, não soma. É o que o modal de programação
 * passa, senão ele mostrava "0 disponível · faltam 20" para as peças que o
 * próprio pedido tinha reservado.
 */
export function useStockBalances(filtros?: { kind?: string; incluirZerados?: boolean; orderId?: string | null }) {
  const params = new URLSearchParams();
  if (filtros?.kind) params.set('kind', filtros.kind);
  if (filtros?.incluirZerados) params.set('incluir_zerados', 'true');
  if (filtros?.orderId) params.set('order_id', filtros.orderId);
  const qs = params.toString();

  return useQuery({
    queryKey: stockKeys.balances(filtros),
    queryFn: () => chamarEstoque<SaldoMaterial[]>(`/api/stock/balances${qs ? `?${qs}` : ''}`),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** Postos do setor ESTOQUE (0111): MALHA, AVIAMENTO, LISA (= materials.kind) e RECEBIMENTO. */
export type PostoEstoque = 'MALHA' | 'AVIAMENTO' | 'LISA' | 'RECEBIMENTO';
export const LABEL_POSTO: Record<PostoEstoque, string> = {
  MALHA: 'Malhas e tecidos',
  AVIAMENTO: 'Aviamentos',
  LISA: 'Peças lisas',
  RECEBIMENTO: 'Recebimento',
};

/**
 * As abas do posto do usuário. Lidas do servidor (que as lê do banco), não do
 * JWT: o admin tira um posto e a aba some no próximo foco da janela.
 */
export function useMeusPostos() {
  return useQuery({
    queryKey: ['stock', 'meus-postos'],
    queryFn: () => chamarEstoque<{ postos: PostoEstoque[]; todos: boolean; gestor: boolean }>('/api/stock/meus-postos'),
    staleTime: 60_000,
  });
}

/** Fornecedor da lista do GestãoClick (espelho `gc_fornecedores`, 1×/dia). */
export interface FornecedorGc {
  gc_id: string;
  nome: string;
  cnpj_cpf: string | null;
  /** Razão social, fantasia… do mesmo cadastro: a busca acha por eles. Ausente em servidor antigo. */
  outros_nomes?: string[];
}

/**
 * A lista de fornecedores para escolher em vez de digitar (2026-09-23). Muda
 * uma vez por dia no servidor; 10 min de cache bastam. Erro vira lista vazia
 * NA TELA DO CAMPO (o combobox avisa e deixa digitar), não aqui.
 */
export function useFornecedores(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['stock', 'fornecedores'],
    queryFn: () => chamarEstoque<FornecedorGc[]>('/api/stock/fornecedores'),
    staleTime: 600_000,
    refetchOnWindowFocus: false,
    enabled: opts.enabled ?? true,
  });
}

export function useMaterials(kind?: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...stockKeys.materials(), kind ?? 'todos'],
    queryFn: () => chamarEstoque<Material[]>(`/api/stock/materials${kind ? `?kind=${kind}` : ''}`),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    // `enabled: false` = não busca: o modal do item só precisa dos aviamentos
    // quando a seção de gola/punho está na tela (fica montado o tempo todo).
    enabled: opts.enabled ?? true,
  });
}

export function useStockUnits(materialId?: string) {
  return useQuery({
    queryKey: stockKeys.units(materialId),
    queryFn: () =>
      chamarEstoque<StockUnit[]>(`/api/stock/units${materialId ? `?material_id=${materialId}` : ''}`),
    staleTime: 30_000,
    enabled: materialId !== '',
  });
}

/** Os rolos que entraram por UMA conferência (janela "Bipar etiquetas do fornecedor"). */
export function useRolosDoRecebimento(receiptId: string | null) {
  return useQuery({
    // Prefixo ['stock','units'] — o PATCH da etiqueta invalida por ele (CHAVES_FISICAS).
    queryKey: ['stock', 'units', 'recebimento', receiptId] as const,
    queryFn: () => chamarEstoque<StockUnit[]>(`/api/stock/units?recebimento=${receiptId}`),
    staleTime: 0,
    enabled: !!receiptId,
  });
}

/**
 * `pedido` filtra pelo NÚMERO (join com `orders`); `orderId` pelo uuid gravado
 * no ledger — é o que funciona para um pedido já excluído, cuja linha em
 * `orders` não existe mais (atalho da pendência de cancelamento).
 */
export function useStockMovements(
  materialId?: string, limite = 50, pedido?: string | null, orderId?: string | null, busca?: string,
  tipos: readonly string[] = [], kind?: string,
  /** Só os lançamentos com exceção registrada (§13.74). */
  soExcecoes = false,
  /** A partir de quando (ISO) — o corte de "Hoje"/"7 dias" das Exceções do dia. */
  desde?: string,
) {
  const params = new URLSearchParams({ limite: String(limite) });
  const tiposQ = tipos.join(',');
  if (tiposQ) params.set('tipos', tiposQ);
  if (kind) params.set('kind', kind);
  if (soExcecoes) params.set('excecao', '1');
  if (desde) params.set('desde', desde);
  if (materialId) params.set('material_id', materialId);
  if (pedido) params.set('pedido', pedido);
  if (orderId) params.set('order_id', orderId);
  // A busca vai ao SERVIDOR: a lista é paginada lá, e filtrar aqui só olharia
  // a página carregada.
  const q = busca?.trim() ?? '';
  if (q) params.set('q', q);
  return useQuery({
    queryKey: [...stockKeys.movements(materialId), limite, pedido ?? '', orderId ?? '', q, tiposQ, kind ?? '', soExcecoes ? 'excecao' : '', desde ?? ''],
    queryFn: () => chamarEstoque<Movimento[]>(`/api/stock/movements?${params}`),
    staleTime: 15_000,
    // A busca muda a chave a cada 300 ms: sem isto a lista virava
    // "Carregando…" a cada tecla e piscava.
    placeholderData: keepPreviousData,
  });
}

/**
 * O estado ATUAL dos lançamentos que o posto guarda no navegador ("Últimos
 * lançamentos"): a linha que não volta foi excluída; `estornado` é desfeita.
 * Chave sob `['stock','movements']`, então o socket (stock_ledger) a invalida
 * junto com o resto. Erro de leitura NÃO é "sumiu": `data` fica indefinido e
 * quem lê não mexe na lista.
 */
export function useSituacaoDosLancamentos(ids: number[]) {
  const chave = [...ids].sort((a, b) => a - b).join(',');
  return useQuery({
    queryKey: [...stockKeys.movements('sessao'), chave],
    queryFn: () => chamarEstoque<Movimento[]>(`/api/stock/movements?ids=${chave}&limite=50`),
    enabled: ids.length > 0,
    staleTime: 15_000,
  });
}

/**
 * Invalida TODA leitura de estoque — e nada além dela.
 *
 * Exportado como função pura (não só como hook) porque quem escreve e quem
 * precisa ver são máquinas diferentes: o gerente cadastra o material no
 * escritório e o combobox do posto, noutro navegador, tem de passar a
 * oferecê-lo. Por isso o `useProductionCoreSync` chama esta mesma função ao
 * receber os eventos de `materials`/`stock_units`/`stock_ledger` — sem uma
 * segunda lista de chaves para manter em sincronia.
 *
 * Invalida as chaves FÍSICAS (saldo, movimentos, rolos, pacotes, materiais)
 * de uma vez: um único lançamento mexe em várias ao mesmo tempo, e enumerar
 * por evento criaria a chance de esquecer uma. `stockKeys.scan` não é query
 * cacheada (o scan é mutation), então nada de refetch por código bipado.
 *
 * ⚠️ Só invalida, nunca refetch forçado — a lição do storm de 18/mai. E o
 * inverso também vale: as chaves de estoque ficam FORA de
 * `invalidateProductionQueries`, senão todo UPDATE de step refetcharia saldo.
 */
/**
 * ⚠️ SÓ AS CHAVES FÍSICAS — nunca o prefixo `['stock']` inteiro. Abaixo dele
 * vivem também capacidades, fila de chegadas, pendências, demandas, o
 * `order-materials` de cada card de CORTE e a conciliação com o ERP (que
 * cruza o catálogo e chama a API do GestãoClick). Invalidar o prefixo fazia
 * cada bipe do galpão refetchar TUDO isso em toda aba aberta — o storm de
 * 18/mai em miniatura, com o chão de fábrica como gatilho.
 */
const CHAVES_FISICAS = [
  ['stock', 'balances'], ['stock', 'materials'], ['stock', 'units'],
  ['stock', 'movements'], ['stock', 'packages'],
] as const;

// O que uma retirada/devolução/estorno/ajuste também muda, e que só o socket
// atualizava (aba em segundo plano, reconexão): a fila de pendências e o
// contador do sidebar (o lançamento fecha LANCAR_CORTE/DEVOLVER_SOBRA), o bloco
// Materiais do card do corte e a fila do comprador (reserva consumida,
// demanda atendida/reaberta). Chaves EXATAS, nunca o prefixo (ver acima).
// As listas de Notas fiscais do Recebimento trazem a contagem de rolos sem etiqueta e no estoque: bipar uma
// etiqueta ou zerar um rolo as muda (§13.49). Só as três listas, nunca o prefixo `['stock','inbound']`
// (a fila de chegadas chama o GestãoClick).
const CHAVES_DERIVADAS = [
  ['stock', 'pending-tasks'], ['stock-alerts'], ['stock', 'order-materials'], ['stock', 'demands'],
  // As peças cortadas do card do corte (o que o pedido pede × o que já saiu da caixa).
  ['stock', 'cortadas'],
  ['stock', 'inbound', 'aguardando-nf'], ['stock', 'inbound', 'ligados-recentes'], ['stock', 'inbound', 'fechados-sem-nf'],
  // Devolução ao fornecedor (§13.106): o bipe e o "Desfazer" mudam os rolos e os totais dela.
  ['stock', 'devolucoes'],
] as const;

export function invalidateStockQueries(queryClient: ReturnType<typeof useQueryClient>) {
  for (const chave of CHAVES_FISICAS) queryClient.invalidateQueries({ queryKey: chave });
  for (const chave of CHAVES_DERIVADAS) queryClient.invalidateQueries({ queryKey: chave });
}

/** Invalida só o estoque — nunca as queries de produção. */
export function useInvalidarEstoque() {
  const qc = useQueryClient();
  return () => invalidateStockQueries(qc);
}

/**
 * O que voltou do bipe.
 *
 * União discriminada por `tipo` porque a decisão 14 é literal: QUALQUER código
 * mostra o histórico completo, e rolo e pacote não têm os mesmos campos (um é
 * pesado, o outro é contado). Um tipo só, com tudo opcional, faria a tela ler
 * `peso_atual` de um pacote e mostrar "undefined kg" no painel grande.
 */
export interface ResultadoScanUnit {
  tipo: 'UNIT';
  unit: StockUnit & { material_nome: string; unit: string; tracking: string; kind: string; nunca_usado?: boolean };
  historico: Movimento[];
  /** Pedidos sugeridos para o rolo (comprado para ele / reservou a malha), com o corte em aberto. */
  sugestoes?: Array<{ order_id: string; step_id: string; numero: string; cliente: string | null; motivo: 'COMPRA' | 'RESERVA'; status?: string; data_fim?: string | null; regularizacao?: boolean }>;
}

export interface ResultadoScanPacote {
  tipo: 'PACKAGE';
  pacote: StockPackage & { material_nome: string; unit: string; tracking: string; kind: string; nunca_usado?: boolean };
  historico: Movimento[];
}

/** Kit gola + punho (§13.92): a etiqueta K junta os pacotes do mesmo pedido; cada um com o próprio saldo. */
export interface ResultadoScanKit {
  tipo: 'KIT';
  kit: {
    barcode: string;
    origem_numero: string | null;
    pacotes: Array<StockPackage & { material_nome: string; unit: string; tracking: string; kind: string; origem_numero?: string | null }>;
  };
  historico: Movimento[];
}

/** O que há dentro de uma caixa de peças cortadas (0139): um pacote por SKU, de modelos, tamanhos e partes misturados. */
export interface PacoteDaCaixaScan {
  package_id: string;
  barcode: string;
  material_id: string;
  material_nome: string;
  modelo: string | null;
  tecido: string | null;
  cor: string | null;
  tamanho: string | null;
  parte: ParteCortada | null;
  qty_atual: number;
}

/** Caixa de peças cortadas: a etiqueta C junta o que está dentro; cada linha tem o próprio saldo. */
export interface ResultadoScanCaixa {
  tipo: 'CAIXA';
  caixa: { id: string; barcode: string; descricao: string | null; pacotes: PacoteDaCaixaScan[] };
  historico: Movimento[];
}

export type ResultadoScan = ResultadoScanUnit | ResultadoScanPacote | ResultadoScanKit | ResultadoScanCaixa;

export function useScan() {
  return useMutation({
    mutationFn: (barcode: string) =>
      chamarEstoque<ResultadoScan>(`/api/stock/scan/${encodeURIComponent(barcode)}`),
  });
}

export interface MovimentoInput {
  type: 'RETIRADA_CORTE' | 'DEVOLUCAO_SOBRA';
  barcode?: string;
  material_id?: string;
  qty?: number;
  order_id?: string;
  production_step_id?: string;
  confirmar_plausibilidade?: boolean;
  /** Retirar mesmo com o material contado para outro pedido (RESERVADO_PARA_OUTRO_PEDIDO). */
  confirmar_reserva?: boolean;
  /** Para quê, quando a retirada sai fora do fluxo (sem pedido / corte fechado há dias) — §13.74. Opcional. */
  motivo?: { tipo: MotivoRetirada; nota?: string };
  /** Um rolo para VÁRIOS pedidos (2 a 10), no lugar de order_id/production_step_id (§13.58). */
  pedidos?: Array<{ order_id: string; production_step_id?: string }>;
}

/** Prévia do rateio: a mesma conta que a retirada grava (§13.58). */
export interface PreviaRateio {
  peso: number;
  unidade: string;
  material_nome: string;
  malha: { tecido: string | null; cor: string | null };
  pedidos: Array<{
    order_id: string;
    numero: string | null;
    pecas: number;
    /** null quando algum pedido não tem peça que case — aí não há divisão. */
    qty: number | null;
    itens_sem_casar: Array<{ item_number: number | null; tecido: string | null; cor: string | null; pecas: number }>;
  }>;
}

export function usePreviaRateio(barcode: string | null, orderIds: string[]) {
  const ids = orderIds.join(',');
  return useQuery({
    queryKey: ['stock', 'rateio', barcode, ids],
    enabled: !!barcode && orderIds.length >= 2,
    queryFn: () => chamarEstoque<PreviaRateio>(
      `/api/stock/rateio?barcode=${encodeURIComponent(barcode ?? '')}&pedidos=${encodeURIComponent(ids)}`,
    ),
    placeholderData: keepPreviousData,
  });
}

export function useMovimentar() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: (input: MovimentoInput) =>
      chamarEstoque<Record<string, unknown>>('/api/stock/movements', { method: 'POST', body: input }),
    onSuccess: () => invalidar(),
  });
}

/** Saída de gola/punho em PACOTE para o pedido (§13.86): o posto bipa a etiqueta do pacote. */
export interface SaidaPacoteAviamentoInput {
  barcode: string;
  qty: number;
  order_id?: string;
  production_step_id?: string;
  confirmar_reserva?: boolean;
  confirmar_baixa_automatica?: boolean;
  motivo?: { tipo: MotivoRetirada; nota?: string };
}

export function useSaidaPacoteAviamento() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: (input: SaidaPacoteAviamentoInput) =>
      chamarEstoque<Record<string, unknown>>('/api/stock/aviamentos/saida-pacote', { method: 'POST', body: input }),
    onSuccess: () => invalidar(),
  });
}

export interface CriarUnitInput {
  material_id: string;
  peso: number;
  batch_id: string;
  supplier_barcode?: string;
  fornecedor_texto?: string;
  /** Escolhido da lista do GestãoClick; ausente = "outro" (só o texto). */
  gc_fornecedor_id?: string;
  valor_unitario?: number;
  justification?: string;
  /** Contagem (INVENTARIO, padrão) ou malha que voltou (DEVOLUCAO → ENTRADA_DEVOLUCAO). */
  origem?: 'INVENTARIO' | 'DEVOLUCAO';
}

/**
 * Corrige atributos do rolo que NÃO são saldo: fornecedor e custo unitário
 * (o rolo nasce com o fornecedor do cabeçalho da conferência; quando o
 * cabeçalho estava errado, é aqui que se conserta). Só o que vier no objeto muda.
 */
export function useEditarRolo() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: ({ unitId, ...campos }: {
      unitId: string; fornecedor_texto?: string | null; gc_fornecedor_id?: string | null; valor_unitario?: number | null;
    }) =>
      chamarEstoque<StockUnit>(`/api/stock/units/${unitId}`, { method: 'PATCH', body: campos }),
    onSuccess: () => invalidar(),
  });
}

/** Rolo cadastrado no material errado: mantém código e história, o peso troca de material no ledger. */
export function useReclassificarRolo() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: ({ unitId, material_id, justification }: { unitId: string; material_id: string; justification: string }) =>
      chamarEstoque<{ unit_id: string; barcode: string; de: string; para: string; peso: number }>(
        `/api/stock/units/${unitId}/reclassificar`, { method: 'POST', body: { material_id, justification } },
      ),
    onSuccess: () => invalidar(),
  });
}

/** O estoque de um material por FORNECEDOR do rolo (§13.80): a prévia de "Separar por fornecedor". */
export interface FornecedoresDoMaterial {
  material: { id: string; kind: string; tracking: string; name: string; unit: string; fornecedor: string | null };
  grupos: Array<{ chave: string; fornecedor: string | null; rolos: number; peso: number }>;
  saldo: number;
  reservado: number;
}

export function useFornecedoresDoMaterial(materialId: string | null, enabled = true) {
  return useQuery({
    queryKey: [...stockKeys.materials(), 'fornecedores', materialId ?? ''],
    queryFn: () => chamarEstoque<FornecedoresDoMaterial>(`/api/stock/materials/${materialId}/fornecedores`),
    enabled: enabled && !!materialId,
    staleTime: 0,
  });
}

/** Move os rolos dos fornecedores escolhidos para o material de cada um (cria o que falta). Ledger intacto. */
export function useSepararPorFornecedor() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: ({ materialId, fornecedores }: { materialId: string; fornecedores: string[] }) =>
      chamarEstoque<{
        movidos: Array<{ fornecedor: string; material_id: string; material: string; rolos: number; peso: number; criado: boolean }>;
        saldo_restante: number; reservado: number;
      }>(`/api/stock/materials/${materialId}/separar-por-fornecedor`, { method: 'POST', body: { fornecedores } }),
    onSuccess: () => invalidar(),
  });
}

/** Associa (ou troca) a etiqueta do fornecedor de um rolo já cadastrado. */
export function useAssociarEtiqueta() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: ({ unitId, supplier_barcode }: { unitId: string; supplier_barcode: string | null }) =>
      chamarEstoque<{ id: string; barcode: string; supplier_barcode: string | null }>(
        `/api/stock/units/${unitId}`, { method: 'PATCH', body: { supplier_barcode } },
      ),
    onSuccess: () => invalidar(),
  });
}

export function useCriarUnit() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: (input: CriarUnitInput) =>
      chamarEstoque<{ unit: StockUnit; material: Material }>('/api/stock/units', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => invalidar(),
  });
}

/**
 * Exclusão FÍSICA de um pacote/rolo nunca usado — admin, com auditoria (0108).
 * O objeto e todos os seus lançamentos somem; `ja_excluido` é o retry.
 */
export interface ResultadoSemeioGolas {
  dry_run: boolean;
  /** `puladas` = itens de cor INATIVA em cor_gola (não criados); total = novos + existentes + puladas. */
  materiais: { total: number; novos: number; existentes: number; puladas: number };
  catalogo: { novas: number; existentes: number };
  /** Cores do piquet sem par na cor da malha: nascem na grafia do fornecedor. */
  pa_sem_par_na_malha: string[];
  /** Cores inativas em cor_gola: puladas, não reativadas. */
  cores_inativas: string[];
}

/** "Semear golas e punhos" (§13.47): `true` = só a prévia (não escreve). */
export function useSemearGolasPunhos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) =>
      chamarEstoque<ResultadoSemeioGolas>('/api/stock/materials/semear-golas-punhos', { method: 'POST', body: { dry_run: dryRun } }),
    onSuccess: (_r, dryRun) => {
      if (dryRun) return;
      qc.invalidateQueries({ queryKey: stockKeys.materials() });
      qc.invalidateQueries({ queryKey: ['stock', 'balances'] });
      // O catálogo "Cor de gola/punho" ganhou opções: o combobox do item lê daqui.
      qc.invalidateQueries({ queryKey: ['item-catalog'] });
      qc.invalidateQueries({ queryKey: ['item-catalog-admin'] });
    },
  });
}

export function useExcluirObjeto() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: (input: { package_id?: string; unit_id?: string; motivo: string }) =>
      chamarEstoque<{
        ja_excluido?: boolean; objeto?: 'PACOTE' | 'ROLO'; barcode?: string;
        ledger_apagados?: number; saldo_removido?: number; purge_id?: string;
      }>('/api/stock/purges', { method: 'POST', body: input }),
    onSuccess: () => invalidar(),
  });
}

/** O que o admin marcou para "Excluir de vez" em lote (um pacote ou rolo). */
export interface ObjetoParaExcluir {
  tipo: 'PACOTE' | 'ROLO';
  id: string;
  barcode: string;
  material: string;
  saldo: number;
  unidade?: string;
}

/** O desfecho de UM objeto no lote. `retentavel`: falha de rede/concorrência, não de regra. */
export interface DesfechoExclusao {
  barcode: string;
  estado: 'ok' | 'ja' | 'recusado';
  mensagem?: string;
  retentavel?: boolean;
  objeto: ObjetoParaExcluir;
}

/**
 * "Excluir de vez" em lote (§13.65): chama a MESMA rota `/purges` para cada
 * objeto, um por vez. Não há rota nova de propósito — a elegibilidade, os
 * locks, a auditoria e o retry idempotente (`ja_excluido`) vivem lá, e uma
 * segunda implementação do apagar divergiria da primeira. Cada objeto é uma
 * transação própria: o que o servidor recusa (já usado) não impede os demais.
 * Invalida o estoque UMA vez no fim, em vez de a cada chamada.
 */
export function useExcluirEmLote() {
  const invalidar = useInvalidarEstoque();
  return async (
    objetos: ObjetoParaExcluir[],
    motivo: string,
    aoAndar?: (feitos: number) => void,
  ): Promise<DesfechoExclusao[]> => {
    const desfechos: DesfechoExclusao[] = [];
    try {
      for (const objeto of objetos) {
        try {
          const r = await chamarEstoque<{ ja_excluido?: boolean }>('/api/stock/purges', {
            method: 'POST',
            body: { [objeto.tipo === 'PACOTE' ? 'package_id' : 'unit_id']: objeto.id, motivo },
          });
          desfechos.push({ barcode: objeto.barcode, estado: r?.ja_excluido ? 'ja' : 'ok', objeto });
        } catch (err) {
          const doServidor = err instanceof StockError;
          desfechos.push({
            barcode: objeto.barcode,
            estado: 'recusado',
            mensagem: doServidor ? err.message : 'Sem resposta do servidor — tente de novo.',
            // Regra do negócio (já usado…) não muda ao repetir; rede e fila sim.
            retentavel: !doServidor || err.code === 'ESTADO_ALTERADO',
            objeto,
          });
        }
        aoAndar?.(desfechos.length);
      }
    } finally {
      invalidar();
    }
    return desfechos;
  };
}

/** Exclusão física do par movimento + estorno (0110) — admin. */
export function useExcluirMovimento() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: (input: { ledger_id: number; motivo: string }) =>
      chamarEstoque<{ ja_excluido?: boolean; ledger_apagados?: number[]; purge_id?: string }>(
        '/api/stock/purges/movimento', { method: 'POST', body: input }),
    onSuccess: () => invalidar(),
  });
}

export function useAjustar() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: (input: {
      barcode?: string;
      material_id?: string;
      contagem: number;
      justification: string;
      /** Chip da tela (perda, avaria, erro de contagem…) — vai para o metadata do lançamento. */
      motivo_tipo?: string | null;
      type?: 'AJUSTE' | 'INVENTARIO';
      batch_id?: string;
      /** Devolução ao fornecedor (§13.106): o rolo sai a zero com o motivo fixo, ligado a esta devolução. */
      devolucao_id?: string;
    }) => chamarEstoque<Record<string, unknown> & { aviso_reserva?: AvisoReservaDescoberta }>(
      '/api/stock/adjustments', { method: 'POST', body: input }),
    onSuccess: () => invalidar(),
  });
}

/** As devoluções ao fornecedor: as sem NF e as com NF dos últimos 60 dias (§13.106). */
export function useDevolucoesFornecedor(ativo = true) {
  return useQuery({
    queryKey: stockKeys.devolucoes(),
    queryFn: async () =>
      (await chamarEstoque<{ devolucoes: DevolucaoFornecedor[] }>('/api/stock/devolucoes')).devolucoes,
    enabled: ativo,
    staleTime: 15_000,
  });
}

export function useAbrirDevolucao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => chamarEstoque<{ id: string }>('/api/stock/devolucoes', { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: stockKeys.devolucoes() }),
  });
}

export function useLigarNfDevolucao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { devolucaoId: string; numero: string; loja_id?: string; gc_id?: string }) =>
      chamarEstoque<{ devolucao: DevolucaoFornecedor | null }>(
        `/api/stock/devolucoes/${input.devolucaoId}/nf`,
        { method: 'POST', body: { numero: input.numero, loja_id: input.loja_id, gc_id: input.gc_id } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: stockKeys.devolucoes() }),
  });
}

export function useEstornar() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: (input: { ledger_id: number; justification: string; qty?: number }) =>
      chamarEstoque<Record<string, unknown>>('/api/stock/estornos', { method: 'POST', body: input }),
    onSuccess: () => invalidar(),
  });
}

export function useCriarMaterial() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: (input: Partial<Material>) =>
      chamarEstoque<Material>('/api/stock/materials', { method: 'POST', body: input }),
    onSuccess: () => invalidar(),
  });
}

export function useAtualizarMaterial() {
  const invalidar = useInvalidarEstoque();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Material> & { id: string }) =>
      chamarEstoque<Material>(`/api/stock/materials/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidar(),
  });
}

/**
 * A identidade do material (tipo, controle, unidade, tecido, cor, modelo,
 * tamanho) congela no primeiro vínculo: o ledger guarda números sem unidade e
 * os atributos são a chave de casamento com o item do pedido. Esta consulta
 * diz se já há vínculo e qual — é o que a tela usa para travar os campos ANTES
 * do envio, em vez de deixar o operador preencher e tomar MATERIAL_EM_USO.
 *
 * `staleTime: 0`: entre abrir o formulário e salvar, alguém no galpão pode ter
 * bipado o primeiro rolo. Cache aqui seria trava desatualizada nos dois
 * sentidos — e o servidor confere de novo de qualquer forma.
 */
export function useMaterialEmUso(id: string | null | undefined) {
  return useQuery({
    queryKey: ['stock', 'material-uso', id ?? ''],
    queryFn: () =>
      chamarEstoque<{ em_uso: boolean; motivo: string | null }>(
        `/api/stock/materials/${encodeURIComponent(id as string)}/uso`,
      ),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    // Uma tentativa extra e para. Com o padrão (3, com backoff) a tela ficava
    // em "Conferindo se este material já tem movimento…" por vários segundos
    // antes de dizer que não deu — e os campos travados o tempo todo.
    retry: 1,
  });
}
